import { PROJECT_SUBTASK_INDEX_QUERY_KEY } from '@/components/projects/hooks/useProjectSubtaskIndex'
import { tasksApi } from '@/lib/api/tasks.api'
import { createLogger } from '@/lib/logger'
import { postMessage } from '@/lib/realtime/broadcast'
import { useOrganizationStore } from '@/store/organization.store'
import { buildUpdatePayload } from '@/utils/saveCustomField'
import { useTaskEventSystem } from '../useTaskEventSystem'
/**
 * Task mutation hooks using factory pattern with specialized operations
 * Reduced from 532 lines to ~350 lines (34% reduction)
 * All features preserved: CRUD, field updates, subtasks, bulk ops, assign/unassign
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import type {
  CreateTask,
  TaskWithAssignments,
  UpdateTaskInput,
} from '@scrambl/shared'

import { queryKeys } from '../query-keys'
import { createResourceMutations } from './createResourceMutations'

const logger = createLogger('hooks/mutations/useTaskMutations')

// Types for task mutations
type TaskCreate = CreateTask
type TaskUpdate = UpdateTaskInput
type TaskPatch = Partial<TaskWithAssignments>

function stripUndefinedValues<T extends Record<string, unknown>>(
  payload: T,
): Partial<T> {
  const cleaned: Partial<T> = {}
  Object.entries(payload).forEach(([key, value]) => {
    if (value !== undefined) {
      cleaned[key as keyof T] = value as T[keyof T]
    }
  })
  return cleaned
}

function mergeTaskPatch(
  existingTask: TaskWithAssignments,
  patch: TaskPatch,
): TaskWithAssignments {
  const sanitizedPatch = stripUndefinedValues(patch as Record<string, unknown>)
  const merged = {
    ...existingTask,
    ...sanitizedPatch,
  } as TaskWithAssignments

  if (Object.prototype.hasOwnProperty.call(sanitizedPatch, 'groupId')) {
    const nextGroupId = sanitizedPatch.groupId ?? null
    const patchGroup = sanitizedPatch.group as TaskWithAssignments['group']
    const existingGroup = existingTask.group
    merged.group =
      patchGroup && patchGroup.id === nextGroupId
        ? patchGroup
        : existingGroup && existingGroup.id === nextGroupId
          ? existingGroup
          : null
  }

  if (
    !Object.prototype.hasOwnProperty.call(
      sanitizedPatch,
      'availableStatuses',
    ) &&
    existingTask.availableStatuses !== undefined
  ) {
    merged.availableStatuses = existingTask.availableStatuses
  }

  return merged
}

function mergeTaskWithExisting(
  existingTask: unknown,
  patch: TaskPatch,
): TaskWithAssignments {
  if (!existingTask || typeof existingTask !== 'object') {
    return patch as TaskWithAssignments
  }

  return mergeTaskPatch(existingTask as TaskWithAssignments, patch)
}

/**
 * Creates a cache updater function that handles all three task cache formats:
 * 1. useTaskData format: { tasks: [...], totalCount, ... }
 * 2. Paginated format: { data: [...], meta: {...} }
 * 3. Direct array format: TaskWithAssignments[]
 */
function createTaskCacheUpdater(
  taskId: string,
  updateFn: (task: TaskWithAssignments) => TaskPatch,
) {
  return (oldData: unknown): unknown => {
    if (!oldData) return oldData

    // Handle useTaskData format { tasks: [...], totalCount, ... }
    if (
      typeof oldData === 'object' &&
      'tasks' in oldData &&
      Array.isArray((oldData as { tasks: unknown[] }).tasks)
    ) {
      const typed = oldData as {
        tasks: TaskWithAssignments[]
        totalCount?: number
      }
      return {
        ...typed,
        tasks: typed.tasks.map(task =>
          task.id === taskId ? mergeTaskPatch(task, updateFn(task)) : task,
        ),
        _updatedAt: Date.now(),
      }
    }

    // Handle paginated response format { data: [...], meta: {...} }
    if (
      typeof oldData === 'object' &&
      'data' in oldData &&
      Array.isArray((oldData as { data: unknown[] }).data)
    ) {
      const typed = oldData as { data: TaskWithAssignments[] }
      return {
        ...typed,
        data: typed.data.map(task =>
          task.id === taskId ? mergeTaskPatch(task, updateFn(task)) : task,
        ),
      }
    }

    // Handle direct array format
    if (Array.isArray(oldData)) {
      return oldData.map(task =>
        task.id === taskId
          ? mergeTaskPatch(
              task as TaskWithAssignments,
              updateFn(task as TaskWithAssignments),
            )
          : task,
      )
    }

    return oldData
  }
}

// Helper to invalidate task-related queries based on task data
function invalidateTaskRelatedQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  task: TaskWithAssignments,
  oldParentId?: string,
) {
  // Invalidate groups if task has module context
  if (task.moduleType && task.moduleId) {
    queryClient.invalidateQueries({
      queryKey: queryKeys.tasks.groups({
        moduleType: task.moduleType,
        moduleId: task.moduleId,
      }),
    })
  }

  // Invalidate subtasks queries for parent changes
  // Note: Using ['subtasks', parentId] format for consistency with useSubtasksQuery
  if (oldParentId && oldParentId !== task.parentTaskId) {
    queryClient.invalidateQueries({ queryKey: ['subtasks', oldParentId] })
  }
  if (task.parentTaskId) {
    queryClient.invalidateQueries({ queryKey: ['subtasks', task.parentTaskId] })
  }

  // The project subtask index decides whether a parent surfaces as a filter
  // context row. Creates, deletes and parent moves add or remove rows, which a
  // cache patch cannot express — refetch instead.
  queryClient.invalidateQueries({
    queryKey: [PROJECT_SUBTASK_INDEX_QUERY_KEY],
  })
}

// Core CRUD using factory (create uses custom wrapper for API signature)
const taskMutations = createResourceMutations<
  TaskWithAssignments,
  TaskCreate,
  TaskUpdate
>({
  resourceName: 'Task',
  resourceNameSingular: 'task',
  queryKeyFactory: queryKeys.tasks,
  api: {
    create: (data: TaskCreate) => tasksApi.createTask(data),
    update: (id: string, data: TaskUpdate) => tasksApi.updateTask(id, data),
    updateField: (id: string, field: string, value: unknown) => {
      const updateData = buildUpdatePayload(field, value)
      return tasksApi.updateTask(id, updateData)
    },
    delete: (id: string) => tasksApi.deleteTask(id),
  },
  invalidation: {
    onCreate: {
      additionalInvalidations: [() => queryKeys.tasks.groups()],
    },
    onUpdate: {
      additionalInvalidations: [() => queryKeys.tasks.groups()],
    },
    onDelete: {
      additionalInvalidations: [
        () => queryKeys.tasks.groups(),
        () => ['my-tasks'], // Invalidate my-tasks cache for immediate UI update
      ],
    },
  },
  enableRealtime: true,
})

// Export create with wrapper for backward-compatible API signature
export function useCreateTaskMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ taskData }: { taskData: TaskCreate; tempId?: string }) =>
      tasksApi.createTask(taskData),
    onMutate: async () => {
      // Abort stale in-flight list requests so old snapshots cannot overwrite
      // freshly created tasks in shared caches.
      await queryClient.cancelQueries({ queryKey: ['my-tasks'] })
      await queryClient.cancelQueries({ queryKey: ['tasks'] })
    },
    onSuccess: (newTask, variables) => {
      const { tempId } = variables

      // Register the created task ID so real-time handler skips duplicate
      const tracker = ((
        window as unknown as Record<string, Map<string, number>>
      ).__taskCreationTracker ??= new Map<string, number>())
      tracker.set(`created-${newTask.id}`, Date.now())

      // If we have a tempId, replace the optimistic task with the real one in cache
      // This prevents flickering when multiple tasks are being created
      if (tempId) {
        queryClient.setQueriesData({ queryKey: ['tasks'] }, (oldData: any) => {
          if (!oldData) return oldData

          // Handle useTaskData format { tasks: [...], totalCount, ... }
          if (
            oldData &&
            typeof oldData === 'object' &&
            'tasks' in oldData &&
            Array.isArray(oldData.tasks)
          ) {
            const tempIndex = oldData.tasks.findIndex(
              (task: any) => task.id === tempId,
            )
            if (tempIndex === -1) {
              return oldData
            }

            const nextTasks = [...oldData.tasks]
            nextTasks[tempIndex] = mergeTaskWithExisting(
              nextTasks[tempIndex],
              newTask,
            )
            return {
              ...oldData,
              tasks: nextTasks,
            }
          }

          // Handle paginated response format { data: [...], meta: {...} }
          if (
            oldData &&
            typeof oldData === 'object' &&
            'data' in oldData &&
            Array.isArray(oldData.data)
          ) {
            const tempIndex = oldData.data.findIndex(
              (task: any) => task.id === tempId,
            )
            if (tempIndex === -1) {
              return oldData
            }

            const nextData = [...oldData.data]
            nextData[tempIndex] = mergeTaskWithExisting(
              nextData[tempIndex],
              newTask,
            )
            return {
              ...oldData,
              data: nextData,
            }
          }

          // Handle direct array format
          if (Array.isArray(oldData)) {
            const tempIndex = oldData.findIndex(
              (task: any) => task.id === tempId,
            )
            if (tempIndex === -1) {
              return oldData
            }

            const nextData = [...oldData]
            nextData[tempIndex] = mergeTaskWithExisting(
              nextData[tempIndex],
              newTask,
            )
            return nextData
          }

          return oldData
        })
      } else {
        // Fallback to invalidation for tasks created without tempId
        queryClient.invalidateQueries({ queryKey: queryKeys.tasks.lists() })
      }

      // Always keep my-tasks queries fresh (Today widget, My Tasks page, tray tasks)
      queryClient.setQueriesData({ queryKey: ['my-tasks'] }, (oldData: any) => {
        if (!oldData) return oldData

        if (
          oldData &&
          typeof oldData === 'object' &&
          'tasks' in oldData &&
          Array.isArray(oldData.tasks)
        ) {
          const existingIndex = oldData.tasks.findIndex(
            (task: any) => task.id === newTask.id,
          )
          const tempIndex = tempId
            ? oldData.tasks.findIndex((task: any) => task.id === tempId)
            : -1

          if (tempIndex !== -1) {
            const nextTasks = [...oldData.tasks]
            nextTasks[tempIndex] = mergeTaskWithExisting(
              nextTasks[tempIndex],
              newTask,
            )
            return { ...oldData, tasks: nextTasks }
          }

          if (existingIndex !== -1) {
            const nextTasks = [...oldData.tasks]
            nextTasks[existingIndex] = mergeTaskWithExisting(
              nextTasks[existingIndex],
              newTask,
            )
            return { ...oldData, tasks: nextTasks }
          }

          return {
            ...oldData,
            tasks: [newTask, ...oldData.tasks],
            totalCount: (oldData.totalCount || 0) + 1,
          }
        }

        return oldData
      })
      // Do not trigger an immediate refetch here.
      // We already merge `newTask` into my-tasks caches above, and under slow network
      // a refetch can resolve with an older snapshot and temporarily drop newly created tasks.
      // Mark stale only; normal background refetch/revalidation will reconcile safely.
      queryClient.invalidateQueries({
        queryKey: ['my-tasks'],
        refetchType: 'none',
      })

      queryClient.setQueryData(
        queryKeys.tasks.detail(newTask.id, { includeCustomFields: true }),
        (oldData: unknown) => mergeTaskWithExisting(oldData, newTask),
      )
      invalidateTaskRelatedQueries(queryClient, newTask)
      postMessage('tasks:created', {
        id: newTask.id,
        task: newTask,
        ts: Date.now(),
      })
    },
    onError: (error, variables) => {
      const { tempId } = variables

      // If we have a tempId, remove the optimistic task from cache on error
      if (tempId) {
        queryClient.setQueriesData({ queryKey: ['tasks'] }, (oldData: any) => {
          if (!oldData) return oldData

          // Handle useTaskData format { tasks: [...], totalCount, ... }
          if (
            oldData &&
            typeof oldData === 'object' &&
            'tasks' in oldData &&
            Array.isArray(oldData.tasks)
          ) {
            return {
              ...oldData,
              tasks: oldData.tasks.filter((task: any) => task.id !== tempId),
              totalCount: Math.max(0, (oldData.totalCount || 0) - 1),
            }
          }

          // Handle paginated response format { data: [...], meta: {...} }
          if (
            oldData &&
            typeof oldData === 'object' &&
            'data' in oldData &&
            Array.isArray(oldData.data)
          ) {
            return {
              ...oldData,
              data: oldData.data.filter((task: any) => task.id !== tempId),
            }
          }

          // Handle direct array format
          if (Array.isArray(oldData)) {
            return oldData.filter((task: any) => task.id !== tempId)
          }

          return oldData
        })

        queryClient.setQueriesData(
          { queryKey: ['my-tasks'] },
          (oldData: any) => {
            if (!oldData) return oldData

            if (
              oldData &&
              typeof oldData === 'object' &&
              'tasks' in oldData &&
              Array.isArray(oldData.tasks)
            ) {
              const filteredTasks = oldData.tasks.filter(
                (task: any) => task.id !== tempId,
              )
              const removedCount = oldData.tasks.length - filteredTasks.length
              return {
                ...oldData,
                tasks: filteredTasks,
                totalCount: Math.max(
                  0,
                  (oldData.totalCount || 0) - removedCount,
                ),
              }
            }

            return oldData
          },
        )
      }

      logger.error('[useCreateTaskMutation] Error:', error)
      toast.error(
        error instanceof Error ? error.message : 'Failed to create task',
      )
    },
  })
}

// Export update with additional parent tracking logic
export function useUpdateTaskMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: TaskUpdate }) =>
      tasksApi.updateTask(id, data),
    onSuccess: (updatedTask, variables) => {
      queryClient.setQueryData(
        queryKeys.tasks.detail(variables.id, { includeCustomFields: true }),
        (oldData: unknown) => mergeTaskWithExisting(oldData, updatedTask),
      )
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.lists() })
      // Also invalidate my-tasks cache (used by MyTasksWidget and MyTasksTable)
      queryClient.invalidateQueries({ queryKey: ['my-tasks'] })
      invalidateTaskRelatedQueries(
        queryClient,
        updatedTask,
        variables.data.parentTaskId as string | undefined,
      )
      postMessage('tasks:updated', {
        id: variables.id,
        task: updatedTask,
        ts: Date.now(),
      })
      toast.success('Task updated successfully')
    },
    onError: error => {
      logger.error('[useUpdateTaskMutation] Error:', error)
      toast.error(
        error instanceof Error ? error.message : 'Failed to update task',
      )
    },
  })
}

/**
 * Promote a subtask to a standalone top-level task. Pass the previous parent's
 * id so both the old parent's subtask list and the top-level lists refresh.
 */
export function usePromoteSubtaskMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id }: { id: string; oldParentId?: string }) =>
      tasksApi.promoteSubtask(id),
    onSuccess: (updatedTask, variables) => {
      queryClient.setQueryData(
        queryKeys.tasks.detail(variables.id, { includeCustomFields: true }),
        (oldData: unknown) => mergeTaskWithExisting(oldData, updatedTask),
      )
      // Broad ['tasks'] invalidation so the positional list keys the project /
      // board / bucket tables use (['tasks', moduleType, moduleId, ...]) refetch
      // — tasks.lists() (['tasks','list']) does NOT prefix-match those.
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.all })
      queryClient.invalidateQueries({ queryKey: ['task-groups'] })
      queryClient.invalidateQueries({ queryKey: ['my-tasks'] })
      invalidateTaskRelatedQueries(
        queryClient,
        updatedTask,
        variables.oldParentId,
      )
      postMessage('tasks:updated', {
        id: variables.id,
        task: updatedTask,
        ts: Date.now(),
      })
      toast.success('Promoted to task')
    },
    onError: error => {
      logger.error('[usePromoteSubtaskMutation] Error:', error)
      toast.error(
        error instanceof Error ? error.message : 'Failed to promote subtask',
      )
    },
  })
}

/**
 * Convert a standalone task into a subtask of a (same-project) parent task.
 */
export function useConvertToSubtaskMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, parentTaskId }: { id: string; parentTaskId: string }) =>
      tasksApi.convertToSubtask(id, parentTaskId),
    onSuccess: (updatedTask, variables) => {
      queryClient.setQueryData(
        queryKeys.tasks.detail(variables.id, { includeCustomFields: true }),
        (oldData: unknown) => mergeTaskWithExisting(oldData, updatedTask),
      )
      // Broad ['tasks'] invalidation so the positional list keys the project /
      // board / bucket tables use (['tasks', moduleType, moduleId, ...]) refetch
      // — the converted task drops out of the top-level list and the parent's
      // subtask count updates. tasks.lists() would not prefix-match those keys.
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.all })
      queryClient.invalidateQueries({ queryKey: ['task-groups'] })
      queryClient.invalidateQueries({ queryKey: ['my-tasks'] })
      invalidateTaskRelatedQueries(queryClient, updatedTask)
      postMessage('tasks:updated', {
        id: variables.id,
        task: updatedTask,
        ts: Date.now(),
      })
      toast.success('Converted to subtask')
    },
    onError: error => {
      logger.error('[useConvertToSubtaskMutation] Error:', error)
      toast.error(
        error instanceof Error ? error.message : 'Failed to convert to subtask',
      )
    },
  })
}

export const useDeleteTaskMutation = taskMutations.useDeleteMutation

/**
 * Mutation hook to update a single task field (including custom fields)
 * This is a specialized mutation for field-level updates
 */
export function useUpdateTaskFieldMutation() {
  const queryClient = useQueryClient()
  const eventSystem = useTaskEventSystem()

  return useMutation({
    mutationFn: ({
      id,
      field,
      value,
    }: {
      id: string
      field: string
      value: unknown
    }) => {
      const updateData = buildUpdatePayload(field, value)
      return tasksApi.updateTask(id, updateData)
    },
    onMutate: async ({ id, field, value }) => {
      // Cancel any outgoing refetches to avoid overwriting optimistic update
      // Use broad 'tasks' prefix to catch all task queries (lists, groups, etc.)
      await queryClient.cancelQueries({ queryKey: ['tasks'] })
      await queryClient.cancelQueries({ queryKey: ['my-tasks'] })

      // Snapshot previous values for rollback
      const previousTaskQueries = queryClient.getQueriesData({
        queryKey: ['tasks'],
      })
      const previousMyTaskQueries = queryClient.getQueriesData({
        queryKey: ['my-tasks'],
      })
      const previousSubtaskIndexQueries = queryClient.getQueriesData({
        queryKey: [PROJECT_SUBTASK_INDEX_QUERY_KEY],
      })
      const previousDetailData = queryClient.getQueryData(
        queryKeys.tasks.detail(id, { includeCustomFields: true }),
      )

      // Always apply the user's own edit immediately. The patch only touches
      // the edited row, so open editors on other rows are unaffected — deferral
      // is reserved for remote (realtime) updates, never the user's own action.
      const optimisticUpdater = createTaskCacheUpdater(id, () => ({
        [field]: value,
      }))

      // Optimistically update ALL task caches (lists, groups, etc.)
      queryClient.setQueriesData({ queryKey: ['tasks'] }, optimisticUpdater)

      // Also update my-tasks cache (used by MyTasksWidget and MyTasksTable)
      queryClient.setQueriesData(
        { queryKey: ['my-tasks'] },
        optimisticUpdater,
      )

      // Keep the project subtask index in step, so editing a subtask (assigning
      // someone, renaming, changing status) immediately re-evaluates whether its
      // parent should surface as a filter context row.
      queryClient.setQueriesData(
        { queryKey: [PROJECT_SUBTASK_INDEX_QUERY_KEY] },
        optimisticUpdater,
      )

      // Optimistically update detail cache
      queryClient.setQueryData(
        queryKeys.tasks.detail(id, { includeCustomFields: true }),
        (oldData: any) => {
          if (!oldData) return oldData
          return mergeTaskPatch(
            oldData as TaskWithAssignments,
            { [field]: value } as TaskPatch,
          )
        },
      )

      // Return context for rollback on error
      return {
        previousTaskQueries,
        previousMyTaskQueries,
        previousSubtaskIndexQueries,
        previousDetailData,
      }
    },
    onError: (error, variables, context) => {
      logger.error('[useUpdateTaskFieldMutation] Error:', error)

      // Rollback all task queries
      if (context?.previousTaskQueries) {
        context.previousTaskQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data)
        })
      }

      // Rollback my-tasks queries
      if (context?.previousMyTaskQueries) {
        context.previousMyTaskQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data)
        })
      }

      // Rollback project subtask index queries
      if (context?.previousSubtaskIndexQueries) {
        context.previousSubtaskIndexQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data)
        })
      }

      // Rollback detail cache
      if (context?.previousDetailData) {
        queryClient.setQueryData(
          queryKeys.tasks.detail(variables.id, { includeCustomFields: true }),
          context.previousDetailData,
        )
      }

      toast.error('Failed to update task field')
    },
    onSuccess: (updatedTask, variables) => {
      // The user's own edit always lands immediately. The optimistic value from
      // onMutate is already showing; this swaps in the authoritative server row.

      // Flatten custom fields if server returned them in nested format
      // This ensures cache always has the flattened format expected by the UI
      if (
        updatedTask.customFields &&
        typeof updatedTask.customFields === 'object'
      ) {
        Object.entries(updatedTask.customFields).forEach(
          ([fieldName, value]) => {
            const flattenedKey = `custom_${fieldName}`
            ;(updatedTask as any)[flattenedKey] = value
          },
        )
      }

      // Update the specific task in detail cache with server response
      queryClient.setQueryData(
        queryKeys.tasks.detail(variables.id, { includeCustomFields: true }),
        (oldData: unknown) => mergeTaskWithExisting(oldData, updatedTask),
      )

      // Also update byNumber cache if task has a taskNumber
      // This is crucial for task-detail page which uses useTaskByNumberQuery
      if (updatedTask.taskNumber) {
        queryClient.setQueryData(
          queryKeys.tasks.byNumber(updatedTask.taskNumber),
          (oldData: any) => {
            if (!oldData) return updatedTask
            return mergeTaskWithExisting(oldData, updatedTask)
          },
        )
      }

      // Use shared cache updater to merge server response into cached tasks
      const serverResponseUpdater = createTaskCacheUpdater(
        variables.id,
        () => updatedTask,
      )

      // Update ALL task queries (lists, groups, etc.) with server response
      queryClient.setQueriesData({ queryKey: ['tasks'] }, serverResponseUpdater)

      // Also update my-tasks cache (used by MyTasksWidget and MyTasksTable)
      queryClient.setQueriesData(
        { queryKey: ['my-tasks'] },
        serverResponseUpdater,
      )

      queryClient.setQueriesData(
        { queryKey: [PROJECT_SUBTASK_INDEX_QUERY_KEY] },
        serverResponseUpdater,
      )

      // Cross-tab sync using BroadcastChannel
      postMessage('tasks:updated', {
        id: variables.id,
        task: updatedTask,
        ts: Date.now(),
      })

      eventSystem.dispatchFieldUpdate(
        variables.id,
        variables.field,
        variables.value,
        undefined,
        { source: 'task-field-mutation' },
      )

      // Note: No toast here as field updates are often silent/batch operations
    },
  })
}

// Subtask creation mutation
export function useCreateSubtaskMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      parentTaskId,
      subtaskData,
    }: {
      parentTaskId: string
      subtaskData: TaskCreate
    }) => {
      // Track BEFORE API call so realtime handler can skip duplicate
      // Use content-based key since we don't have the ID yet
      const contentKey = `subtask-${parentTaskId}-${subtaskData.name}-${subtaskData.moduleId}`
      const tracker = ((
        window as unknown as Record<string, Map<string, number>>
      ).__taskCreationTracker ??= new Map<string, number>())
      tracker.set(contentKey, Date.now())

      const result = await tasksApi.createSubtask(parentTaskId, subtaskData)

      // Also track by ID once we have it
      tracker.set(`created-${result.id}`, Date.now())

      return result
    },
    onSuccess: newSubtask => {
      // Store the new subtask in its detail cache
      queryClient.setQueryData(
        queryKeys.tasks.detail(newSubtask.id, { includeCustomFields: true }),
        newSubtask,
      )

      // NOTE: We do NOT invalidate task list queries here to prevent loops.
      // SubtasksSection handles its own optimistic cache updates for the subtasks list.
      // Main task lists exclude subtasks anyway (excludeSubtasks=true).
      //
      // The project subtask index is the exception — it deliberately DOES
      // include subtasks, so a new one has to land there or its parent will
      // never surface as a filter context row. Its key is separate from
      // ['tasks'], so this cannot re-trigger the list-query loop above.
      queryClient.invalidateQueries({
        queryKey: [PROJECT_SUBTASK_INDEX_QUERY_KEY],
      })

      toast.success('Subtask created successfully')
    },
    onError: error => {
      logger.error('[useCreateSubtaskMutation] Error:', error)
      toast.error(
        error instanceof Error ? error.message : 'Failed to create subtask',
      )
    },
  })
}

// Bulk operations mutation
export function useBulkTaskOperationsMutation() {
  const queryClient = useQueryClient()

  return useMutation<
    void,
    Error,
    {
      operation: 'assign' | 'unassign' | 'changeGroup' | 'delete' | 'update'
      taskIds: string[]
      data?: Record<string, unknown>
    }
  >({
    mutationFn: async ({ operation, taskIds, data }) => {
      switch (operation) {
        case 'assign':
          if (!data?.taskId || !data?.userIds)
            throw new Error('taskId and userIds required')
          await tasksApi.bulkAssignToTask(
            data.taskId as string,
            data.userIds as string[],
          )
          break
        case 'unassign':
          if (!data?.taskId || !data?.userIds)
            throw new Error('taskId and userIds required')
          await tasksApi.bulkUnassignFromTask(
            data.taskId as string,
            data.userIds as string[],
          )
          break
        case 'changeGroup':
          if (!data?.groupId) throw new Error('groupId required')
          await tasksApi.bulkChangeGroup(taskIds, data.groupId as string | null)
          break
        case 'delete':
          await tasksApi.bulkDeleteTasks(taskIds)
          break
        case 'update':
          if (!data) throw new Error('Update data required')
          await tasksApi.bulkUpdateTasks(
            taskIds,
            data as unknown as UpdateTaskInput,
          )
          break
        default:
          throw new Error(`Unknown bulk operation: ${operation}`)
      }
    },
    onSuccess: (_, variables) => {
      if (variables.operation === 'delete') {
        const deletedIdSet = new Set(variables.taskIds)

        variables.taskIds.forEach(id => {
          queryClient.removeQueries({ queryKey: ['task', id] })
          queryClient.removeQueries({ queryKey: queryKeys.tasks.detail(id) })
        })

        queryClient.removeQueries({
          predicate: query => {
            if (!Array.isArray(query.queryKey)) return false
            if (query.queryKey[0] !== 'tasks') return false

            const cachedTask = queryClient.getQueryData<TaskWithAssignments>(
              query.queryKey,
            )
            return !!cachedTask?.id && deletedIdSet.has(cachedTask.id)
          },
        })

        queryClient.setQueriesData(
          {
            predicate: query =>
              Array.isArray(query.queryKey) &&
              (query.queryKey[0] === 'tasks' ||
                query.queryKey[0] === 'my-tasks' ||
                query.queryKey[0] === PROJECT_SUBTASK_INDEX_QUERY_KEY),
          },
          oldData => {
            if (!oldData) return oldData
            const prune = (tasks: TaskWithAssignments[]) =>
              tasks.filter(task => !deletedIdSet.has(task.id))

            if (Array.isArray(oldData)) {
              return prune(oldData as TaskWithAssignments[])
            }

            if (typeof oldData !== 'object') return oldData
            const typed = oldData as {
              tasks?: TaskWithAssignments[]
              data?: TaskWithAssignments[]
              totalCount?: number
            }

            if (Array.isArray(typed.tasks)) {
              const tasks = prune(typed.tasks)
              const removedCount = typed.tasks.length - tasks.length
              if (removedCount === 0) return oldData
              return {
                ...typed,
                tasks,
                totalCount:
                  typeof typed.totalCount === 'number'
                    ? Math.max(0, typed.totalCount - removedCount)
                    : typed.totalCount,
              }
            }

            if (Array.isArray(typed.data)) {
              const data = prune(typed.data)
              const removedCount = typed.data.length - data.length
              if (removedCount === 0) return oldData
              return {
                ...typed,
                data,
                totalCount:
                  typeof typed.totalCount === 'number'
                    ? Math.max(0, typed.totalCount - removedCount)
                    : typed.totalCount,
              }
            }

            return oldData
          },
        )

        queryClient.invalidateQueries({
          queryKey: queryKeys.tasks.lists(),
          refetchType: 'inactive',
        })
        queryClient.invalidateQueries({
          queryKey: queryKeys.tasks.groups(),
          refetchType: 'inactive',
        })
      } else {
        queryClient.invalidateQueries({ queryKey: queryKeys.tasks.lists() })
        queryClient.invalidateQueries({ queryKey: queryKeys.tasks.groups() })
      }
      postMessage('tasks:bulk', {
        operation: variables.operation,
        taskIds: variables.taskIds,
        ts: Date.now(),
      })
      toast.success(`Bulk ${variables.operation} completed successfully`)
    },
    onError: error => {
      logger.error('[useBulkTaskOperationsMutation] Error:', error)
      toast.error(
        error instanceof Error ? error.message : 'Bulk operation failed',
      )
    },
  })
}

// Helper for assign/unassign mutations
function createAssignmentMutation(
  apiFn: (taskId: string, userId: string) => Promise<TaskWithAssignments>,
  errorMessage: string,
) {
  return function useAssignmentMutation() {
    const queryClient = useQueryClient()
    return useMutation({
      mutationFn: ({ taskId, userId }: { taskId: string; userId: string }) =>
        apiFn(taskId, userId),
      onSuccess: (updatedTask, variables) => {
        queryClient.setQueryData(
          queryKeys.tasks.detail(variables.taskId, {
            includeCustomFields: true,
          }),
          (oldData: unknown) => mergeTaskWithExisting(oldData, updatedTask),
        )
        queryClient.invalidateQueries({ queryKey: queryKeys.tasks.lists() })
        postMessage('tasks:updated', {
          id: variables.taskId,
          task: updatedTask,
          ts: Date.now(),
        })
      },
      onError: error => {
        logger.error(`[${errorMessage}] Error:`, error)
        toast.error(errorMessage)
      },
    })
  }
}

export const useAssignTaskMutation = createAssignmentMutation(
  tasksApi.assignTaskToUser,
  'Failed to assign task',
)

export const useUnassignTaskMutation = createAssignmentMutation(
  tasksApi.unassignTaskFromUser,
  'Failed to unassign task',
)
